import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faArrowLeft,
  faFile,
  faExternalLinkAlt,
  faFolder,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import WebViewer from '@pdftron/webviewer';
import MicrosoftHttpService from '../../../AppSettings/services/MicrosoftHttpService';
import './Templates.css';
import {
  getFileSystemType,
  getSharePointConfig,
} from '../../../AppSettings/environmentConfig';

const Templates = () => {
  const viewer = useRef(null);
  const [webViewerInstance, setWebViewerInstance] = useState(null);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { siteId, driveId } = getSharePointConfig();
  const fileSystemType = getFileSystemType();
  const [folderHistory, setFolderHistory] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleBackButtonClick = async () => {
    if (folderHistory.length > 0) {
      const updatedHistory = [...folderHistory]; // Copy the history array
      const previousFolder = updatedHistory.pop(); // Remove the last folder
      setFolderHistory(updatedHistory); // Update state with new history
      setCurrentFolder(previousFolder); // Navigate back to the previous folder
    }
  };

  // Initialize WebViewer
  useEffect(() => {
    const initializeWebViewer = async () => {
      const instance = await WebViewer(
        {
          path: '../lib',
          licenseKey: 'your-license-key',
        },
        viewer.current
      );

      setWebViewerInstance(instance);
    };
    if (!webViewerInstance) {
      initializeWebViewer();
    }
  }, [webViewerInstance]);

  // Initialize root templates folder
  const initializeRootFolder = useCallback(async () => {
    try {
      const microsoftService = new MicrosoftHttpService([
        'https://graph.microsoft.com/Files.Read.All',
      ]);

      let data;
      // Removed redundant fileSystemType retrieval
      if (fileSystemType === 'SHAREPOINT') {
        data = await microsoftService.getSharePointDriveRoot(siteId, driveId);
      } else if (fileSystemType === 'ONEDRIVE') {
        data = await microsoftService.getOneDriveRootChildren('SEPT');
      }

      let templatesFolder = data.value.find(
        (item) => item.folder && item.name === 'Templates'
      );

      if (!templatesFolder) {
        // Create Templates folder directly
        const createService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.ReadWrite.All',
        ]);
        if (fileSystemType === 'SHAREPOINT') {
          templatesFolder = await createService.createSharePointFolder(
            siteId,
            driveId,
            'Templates',
            null
          );
        } else if (fileSystemType === 'ONEDRIVE') {
          templatesFolder = await createService.createOneDriveFolder(
            'Templates',
            'SEPT'
          );
        }
      }

      if (templatesFolder) {
        setCurrentFolder(templatesFolder);
      } else {
        console.error('Failed to initialize root Templates folder.');
      }
    } catch (error) {
      console.error('Error initializing root folder:', error);
    }
  }, [siteId, driveId, fileSystemType]);

  const createFolder = useCallback(
    async (folderName) => {
      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.ReadWrite.All',
        ]);

        let newFolder;
        // Removed redundant fileSystemType retrieval
        if (fileSystemType === 'SHAREPOINT') {
          if (folderName === 'Templates') {
            newFolder = await microsoftService.createSharePointFolder(
              siteId,
              driveId,
              folderName,
              null
            );
          } else {
            newFolder = await microsoftService.createSharePointFolder(
              siteId,
              driveId,
              folderName,
              currentFolder.id
            );
          }
        } else if (fileSystemType === 'ONEDRIVE') {
          if (folderName === 'Templates') {
            newFolder = await microsoftService.createOneDriveFolder(
              folderName,
              'SEPT'
            );
          } else {
            newFolder = await microsoftService.createOneDriveFolder(
              folderName,
              null,
              currentFolder.id
            );
          }
        }

        return newFolder;
      } catch (error) {
        console.error('Error creating folder:', error);
        return null;
      }
    },
    [siteId, driveId, currentFolder, fileSystemType]
  );

  const fetchFolderContent = useCallback(
    async (folder) => {
      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.Read.All',
        ]);

        let data;
        // Removed redundant fileSystemType retrieval
        if (fileSystemType === 'SHAREPOINT') {
          data = await microsoftService.getSharePointFolderChildren(
            siteId,
            driveId,
            folder.id
          );
        } else if (fileSystemType === 'ONEDRIVE') {
          data = await microsoftService.getFolderChildren(folder.id);
        }

        const newFolders = data.value.filter((item) => item.folder);
        const newFiles = data.value.filter((item) => item.file);
        setFolders(newFolders);
        setFiles(newFiles);
        setFilteredFolders(newFolders);
        setFilteredFiles(newFiles);
      } catch (error) {
        console.error('Error fetching folder content:', error);
      }
    },
    [siteId, driveId, fileSystemType]
  );

  // Initialize root templates folder on component load
  useEffect(() => {
    initializeRootFolder();
  }, [initializeRootFolder]);

  // Fetch folder contents when currentFolder changes
  useEffect(() => {
    if (currentFolder) {
      fetchFolderContent(currentFolder);
    }
  }, [currentFolder, fetchFolderContent]);
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file && currentFolder) {
      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.ReadWrite.All',
        ]);

        let uploadedFile;
        // Removed redundant fileSystemType retrieval
        if (fileSystemType === 'SHAREPOINT') {
          uploadedFile = await microsoftService.uploadSharePointFile(
            siteId,
            driveId,
            currentFolder.id,
            file.name,
            file
          );
        } else if (fileSystemType === 'ONEDRIVE') {
          uploadedFile = await microsoftService.uploadOneDriveFile(
            currentFolder.id,
            file.name,
            file
          );
        }

        if (uploadedFile) {
          console.log('File uploaded successfully');
          fetchFolderContent(currentFolder); // Refresh the folder content
        } else {
          console.error('Error uploading file');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  };

  const handleCreateSubFolder = async () => {
    if (newFolderName.trim() !== '' && currentFolder) {
      const newSubFolder = await createFolder(newFolderName);
      if (newSubFolder) {
        console.log('Subfolder created successfully:', newSubFolder.name);
        fetchFolderContent(currentFolder);
        setNewFolderName('');
      }
    } else {
      console.error('Folder name is empty or current folder is not set.');
    }
  };

  const handleShowModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewFolderName('');
  };

  const handleModalSubmit = () => {
    if (modalType === 'folder') {
      handleCreateSubFolder();
    }
    handleCloseModal();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  // Add search effect
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredFiles(files);
      setFilteredFolders(folders);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredFiles(
        files.filter((file) => file.name.toLowerCase().includes(query))
      );
      setFilteredFolders(
        folders.filter((folder) => folder.name.toLowerCase().includes(query))
      );
    }
  }, [searchQuery, files, folders]);

  return (
    <div className="template-container">
      <div className="templates-header">
        <div>
          {folderHistory.length > 0 && (
            <button className="add-button" onClick={handleBackButtonClick}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <h2>Templates</h2>
        </div>
        <div className="search-bar">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="templates-actions">
          <div className="templates-dropdown-container" ref={dropdownRef}>
            <button className="add-button" onClick={toggleDropdown}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            {showDropdown && (
              <div className="dropdown-menu">
                <button
                  onClick={() => {
                    handleShowModal('folder');
                    setShowDropdown(false);
                  }}
                >
                  <FontAwesomeIcon icon={faFolder} /> Create Folder
                </button>
                <button
                  onClick={() => {
                    document.getElementById('file-upload').click();
                    setShowDropdown(false);
                  }}
                >
                  <FontAwesomeIcon icon={faFile} /> Upload File
                </button>
              </div>
            )}
          </div>
          <input
            type="file"
            id="file-upload"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      <div className="template-list-container">
        <ul>
          {filteredFolders.map((folder) => (
            <li
              key={folder.id}
              onClick={() => {
                setFolderHistory([...folderHistory, currentFolder]);
                setCurrentFolder(folder);
              }}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faFolder} /> {folder.name}
            </li>
          ))}

          {filteredFiles.map((file) => (
            <li key={file.id}>
              <button>
                <FontAwesomeIcon icon={faFile} /> {file.name}
                <FontAwesomeIcon
                  icon={faExternalLinkAlt}
                  style={{ cursor: 'pointer', marginLeft: '10px' }}
                />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div
        ref={viewer}
        style={{ height: '0vh', width: '0px', display: 'none' }}
      ></div>

      {showModal && (
        <div>
          <div>
            <span onClick={handleCloseModal}>&times;</span>
            <h2>{modalType === 'folder' ? 'Create New Folder' : ''}</h2>
            {modalType === 'folder' && (
              <div>
                <label>Folder Name</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                />
              </div>
            )}
            <button onClick={handleModalSubmit}>Create</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
